import React, { useEffect, useState } from 'react';
import { FaCalculator, FaDownload, FaRedo, FaRupeeSign } from 'react-icons/fa';
import SEO from '../components/SEO';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';

const LOAN_MODES = {
  personal: {
    label: 'Personal Loan',
    description: 'Shorter tenure and higher rates for unsecured customer borrowing.',
    defaultLoanAmount: 500000,
    defaultInterestRate: 14,
    defaultTenureMonths: 36,
    defaultMonthlyIncome: 80000,
    defaultExistingEMI: 0,
    incomeLimit: 0.35,
  },
  car: {
    label: 'Car Loan',
    description: 'Vehicle purchase planning with moderate tenure and lower rates.',
    defaultLoanAmount: 900000,
    defaultInterestRate: 10,
    defaultTenureMonths: 60,
    defaultMonthlyIncome: 90000,
    defaultExistingEMI: 0,
    incomeLimit: 0.45,
  },
  home: {
    label: 'Home Loan',
    description: 'Higher-value housing finance with longer repayment horizon.',
    defaultLoanAmount: 4000000,
    defaultInterestRate: 8.75,
    defaultTenureMonths: 240,
    defaultMonthlyIncome: 120000,
    defaultExistingEMI: 0,
    incomeLimit: 0.5,
  },
};

const LoanEstimatorCalculator = () => {
  const [mode, setMode] = useState('personal');
  const [loanAmount, setLoanAmount] = useState(LOAN_MODES.personal.defaultLoanAmount);
  const [interestRate, setInterestRate] = useState(LOAN_MODES.personal.defaultInterestRate);
  const [tenureMonths, setTenureMonths] = useState(LOAN_MODES.personal.defaultTenureMonths);
  const [monthlyIncome, setMonthlyIncome] = useState(LOAN_MODES.personal.defaultMonthlyIncome);
  const [existingEMI, setExistingEMI] = useState(LOAN_MODES.personal.defaultExistingEMI);
  const [result, setResult] = useState(null);

  const activeMode = LOAN_MODES[mode];

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [loanAmount, interestRate, tenureMonths, monthlyIncome, existingEMI]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const applyModeDefaults = (selectedMode) => {
    const preset = LOAN_MODES[selectedMode] || LOAN_MODES.personal;
    setMode(selectedMode);
    setLoanAmount(preset.defaultLoanAmount);
    setInterestRate(preset.defaultInterestRate);
    setTenureMonths(preset.defaultTenureMonths);
    setMonthlyIncome(preset.defaultMonthlyIncome);
    setExistingEMI(preset.defaultExistingEMI);
  };

  const calculate = () => {
    const principal = Number(loanAmount) || 0;
    const rate = Number(interestRate) || 0;
    const months = Number(tenureMonths) || 0;
    const income = Number(monthlyIncome) || 0;
    const currentEMI = Number(existingEMI) || 0;

    const monthlyRate = rate / 12 / 100;
    const emi = monthlyRate === 0 || months <= 0
      ? (months > 0 ? principal / months : 0)
      : principal * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);

    const totalPayment = emi * months;
    const totalInterest = totalPayment - principal;
    const affordableEMI = Math.max(0, income * activeMode.incomeLimit - currentEMI);
    const eligibleLoanAmount = affordableEMI > 0 && months > 0
      ? (monthlyRate === 0
        ? affordableEMI * months
        : affordableEMI * ((Math.pow(1 + monthlyRate, months) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, months))))
      : 0;
    const repaymentRatio = income > 0 ? ((emi + currentEMI) / income) * 100 : 0;
    const affordabilityGap = principal - eligibleLoanAmount;

    setResult({
      emi,
      totalPayment,
      totalInterest,
      affordableEMI,
      eligibleLoanAmount,
      repaymentRatio,
      affordabilityGap,
      isEligible: emi <= affordableEMI,
    });
  };

  const handleReset = () => {
    applyModeDefaults(mode);
  };

  const handleDownloadPDF = () => {
    if (!result) {
      return;
    }

    generateGenericPDF('Customer Loan Estimation Report', {
      Mode: activeMode.label,
      'Requested Loan Amount': formatCurrency(loanAmount),
      'Interest Rate': `${interestRate}%`,
      'Tenure (Months)': tenureMonths,
      'Monthly Income': formatCurrency(monthlyIncome),
      'Existing EMI': formatCurrency(existingEMI),
      'Estimated EMI': formatCurrency(result.emi),
      'Affordable EMI': formatCurrency(result.affordableEMI),
      'Eligible Loan Amount': formatCurrency(result.eligibleLoanAmount),
      'Total Repayment': formatCurrency(result.totalPayment),
      'Total Interest': formatCurrency(result.totalInterest),
      'Repayment Ratio': `${result.repaymentRatio.toFixed(2)}%`,
    });
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Customer Loan Estimator"
        description="Free customer loan estimator for personal loans, car loans, and home loans. Check EMI, affordability, repayment ratio, and eligible loan amount before applying."
        keywords="loan estimator, customer loan estimator, personal loan calculator, car loan calculator, home loan calculator, loan affordability calculator, eligible loan amount calculator"
        canonicalPath="/loan-estimator"
        calculatorType="Customer Loan Estimator"
        calculatorDescription="Estimate EMI, total repayment, and affordable loan amount for customers across personal, car, and home loan modes."
        faqItems={[
          {
            question: 'What does the Customer Loan Estimator help with?',
            answer: 'It helps you estimate EMI, repayment burden, and the loan amount you can likely afford based on income and existing EMIs.',
          },
          {
            question: 'Can I use this before applying for a loan?',
            answer: 'Yes. It is useful for planning a safe loan amount before approaching a bank or lender.',
          },
          {
            question: 'Does existing EMI affect eligibility?',
            answer: 'Yes. Existing EMIs reduce the income available for a new loan and lower the amount you may be able to borrow.',
          },
          {
            question: 'Is this a final bank approval?',
            answer: 'No. It is an estimate only. Final approval depends on credit score, lender policy, documentation, and other checks.',
          },
        ]}
      />

      <div className="calculator-header">
        <h1><FaCalculator style={{ color: '#2563eb' }} /> Customer Loan Estimator</h1>
        <p>Estimate EMI, affordable loan amount, and repayment burden before you apply.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Loan Type</label>
            <div className="tabs">
              {Object.entries(LOAN_MODES).map(([key, preset]) => (
                <button
                  key={key}
                  className={`tab ${mode === key ? 'active' : ''}`}
                  onClick={() => applyModeDefaults(key)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="info-box" style={{ marginBottom: '20px' }}>
            <h4>{activeMode.label}</h4>
            <p style={{ margin: 0 }}>{activeMode.description}</p>
          </div>

          <div className="input-group">
            <label>Requested Loan Amount (Rs.)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={loanAmount === '' ? '' : loanAmount}
                onChange={(e) => setLoanAmount(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                step="1000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={interestRate === '' ? '' : interestRate}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="0.1"
            />
          </div>

          <div className="input-group">
            <label>Tenure (Months)</label>
            <input
              type="number"
              value={tenureMonths === '' ? '' : tenureMonths}
              onChange={(e) => setTenureMonths(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              step="1"
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>
              {Math.floor((tenureMonths || 0) / 12)} years {(tenureMonths || 0) % 12} months
            </small>
          </div>

          <div className="input-group">
            <label>Monthly Income (Rs.)</label>
            <input
              type="number"
              value={monthlyIncome === '' ? '' : monthlyIncome}
              onChange={(e) => setMonthlyIncome(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="1000"
            />
          </div>

          <div className="input-group">
            <label>Existing EMIs (Rs./month)</label>
            <input
              type="number"
              value={existingEMI === '' ? '' : existingEMI}
              onChange={(e) => setExistingEMI(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="1000"
            />
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div
                className="result-summary"
                style={{
                  background: result.isEligible
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #f59e0b, #d97706)',
                }}
              >
                <h3>{result.isEligible ? 'Likely Affordable' : 'Likely Over Budget'}</h3>
                <div className="amount">{formatCurrency(result.emi)}</div>
                <small>{activeMode.label} EMI</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Affordable EMI</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.affordableEMI)}</span>
                </div>
                <div className="result-item" style={{ background: '#f0fdf4', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Eligible Loan Amount</span>
                  <span className="value" style={{ color: '#16a34a' }}>{formatCurrency(result.eligibleLoanAmount)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Repayment</span>
                  <span className="value">{formatCurrency(result.totalPayment)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Repayment Ratio</span>
                  <span className="value">{result.repaymentRatio.toFixed(2)}%</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Affordability Gap</span>
                  <span className="value">
                    {result.affordabilityGap > 0 ? formatCurrency(result.affordabilityGap) : 'Within limit'}
                  </span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '20px' }}>
                <h4>Quick Interpretation</h4>
                <ul>
                  <li>Loan is likely affordable when the EMI stays within the mode-based income limit after existing EMIs.</li>
                  <li>If the affordability gap is positive, the requested amount is above the estimated safe limit.</li>
                  <li>Actual approval can still differ based on credit score, lender policy, and employment profile.</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoanEstimatorCalculator;